// ---------------------------------------------------------------------------
// Raw AEMET OpenData request-building and two-step-response handling —
// shared by two very different callers, which is why this lives here
// instead of inside either one:
//   - functions/api/aemet/*.ts (Cloudflare Pages Functions): the real,
//     deployed path — imports this from outside `functions/` (Pages
//     Functions bundles the whole dependency graph, not just that folder).
//   - src/model/aemet.ts, ONLY in local dev (see its own comment) as a way
//     to use a freshly-issued AEMET key immediately, before it's configured
//     as a Cloudflare secret — calls AEMET directly from the browser (AEMET
//     OpenData is CORS-open, confirmed against the real API).
//
// Only uses global fetch/Response — nothing Node- or DOM-specific — so it
// runs unchanged in both the browser and the Workers runtime.
//
// AEMET OpenData always answers in two steps: the first call (with api_key)
// returns a small JSON pointing at a temporary "datos" URL; the real payload
// lives there.
// ---------------------------------------------------------------------------

const AEMET_BASE = "https://opendata.aemet.es/opendata/api";
/** Derived from AEMET_BASE (not a separate literal) so the two can't drift apart — see fetchAemetJson's own check on why this exists. */
const AEMET_ORIGIN = new URL(AEMET_BASE).origin;

/**
 * Stable, English, machine-readable — never shown to the user as-is. This
 * file is shared with the Cloudflare Functions (see the header comment
 * above), which have no i18n access and shouldn't need any: they forward
 * `code` verbatim as the JSON error body, and the CLIENT (aemet.ts's
 * `translatableAemetError`) maps it to a real `TranslatableError` in
 * whichever language the app is set to. `message` stays a plain English
 * sentence for server logs / as the ultimate fallback if a caller ever
 * reads `.message` directly instead of going through the code.
 */
type AemetErrorCode = "upstream_error" | "unexpected_response";

export class AemetError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: AemetErrorCode
  ) {
    super(message);
  }
}

export function aemetInventoryUrl(apiKey: string): string {
  return `${AEMET_BASE}/valores/climatologicos/inventarioestaciones/todasestaciones?api_key=${encodeURIComponent(apiKey)}`;
}

/**
 * `stationCodes` can be several — AEMET accepts a comma-separated list in
 * this same `estacion` path segment and returns just those stations
 * (confirmed against the real API: requesting 3 stations together returns
 * exactly those 3, nothing else), so a sub-basin's whole candidate list
 * (main + backups) goes in ONE request instead of one per station. See
 * aemet.ts's `STATION_WINDOW_DAYS` for why this replaced the old
 * `todasestaciones` (whole-country) approach.
 */
export function aemetDailyValuesUrl(
  start: string,
  end: string,
  apiKey: string,
  stationCodes: string[]
): string {
  const stations = stationCodes.map(encodeURIComponent).join(",");
  return `${AEMET_BASE}/valores/climatologicos/diarios/datos/fechaini/${start}T00:00:00UTC/fechafin/${end}T23:59:59UTC/estacion/${stations}?api_key=${encodeURIComponent(apiKey)}`;
}

interface AemetStepOneResponse {
  estado: number;
  datos?: string;
  descripcion?: string;
}

/**
 * AEMET answers both steps as `charset=ISO-8859-15` (confirmed against the
 * real API — every response's `Content-Type` header says so), never UTF-8,
 * even though the body is otherwise valid JSON. `Response.json()`/`.text()`
 * always decode as UTF-8 regardless of the declared charset (per the Fetch
 * spec), so every accented character (á, é, í, ó, ú, ñ, ü — common in
 * station names like "ALBACETE BASE AÉREA") turned into the U+FFFD
 * replacement character ("A�REA"). Decoding the raw bytes with the real
 * charset first fixes this.
 */
async function decodeAemetJson(res: Response): Promise<unknown> {
  const bytes = await res.arrayBuffer();
  const text = new TextDecoder("iso-8859-15").decode(bytes);
  return JSON.parse(text);
}

/**
 * Both the Cloudflare Function callers (daily.ts/stations.ts) and the local-
 * dev browser fallback (aemet.ts) already catch any non-AemetError as a
 * generic "upstream_error"/502 — a hung AEMET request (network blip,
 * unresponsive upstream) would otherwise wait on the platform's/browser's
 * own default timeout with no clean error in the meantime. 15s covers
 * AEMET's real 2-step round trip with margin (both steps observed well
 * under 2s each in practice) without waiting anywhere near a Worker's own
 * wall-clock limit.
 */
const TIMEOUT_MS = 15_000;

export async function fetchAemetJson(url: string, init?: RequestInit): Promise<unknown> {
  const requestInit: RequestInit = {
    ...init,
    signal: init?.signal ?? AbortSignal.timeout(TIMEOUT_MS),
  };
  const stepOneRes = await fetch(url, requestInit);
  if (!stepOneRes.ok) {
    throw new AemetError(
      `AEMET responded ${stepOneRes.status} on the first request`,
      502,
      "upstream_error"
    );
  }
  const stepOne = (await decodeAemetJson(stepOneRes)) as AemetStepOneResponse;
  if (stepOne.estado !== 200 || !stepOne.datos) {
    throw new AemetError(
      stepOne.descripcion ?? "Unexpected AEMET response",
      502,
      "unexpected_response"
    );
  }
  // Defense-in-depth (security audit finding, 2026-08-25): `stepOne.datos`
  // comes from AEMET's own trusted, TLS-protected response, not from any
  // request parameter — not attacker-controlled in today's threat model —
  // but nothing was actually stopping this server-side fetch from following
  // it anywhere if that ever changed (a compromised/misconfigured upstream).
  // Fails the same documented way as an unexpected `estado`/missing `datos`
  // above, rather than a generic crash.
  if (!stepOne.datos.startsWith(`${AEMET_ORIGIN}/`)) {
    throw new AemetError(
      `AEMET's 'datos' URL wasn't on the expected host (${AEMET_ORIGIN})`,
      502,
      "unexpected_response"
    );
  }
  const dataRes = await fetch(stepOne.datos, requestInit);
  if (!dataRes.ok) {
    throw new AemetError(
      `AEMET responded ${dataRes.status} fetching the data`,
      502,
      "upstream_error"
    );
  }
  return decodeAemetJson(dataRes);
}
